import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { concatMap, from, map, Observable, of, toArray } from 'rxjs';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { FiltersService } from '@zaoblako/web/core/services/filters.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ContractService } from '@zaoblako/web/core/services/finance/contract.service';
import { ContractAnalyticalFeaturesQueryInterface } from '@zaoblako/shared/modules/document/dtos/contract-analytical-features/contract-analytical-features-query.dto';
import { StepsModule } from 'primeng/steps';
import { AsyncPipe } from '@angular/common';
import { CardModule } from 'primeng/card';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { ContractAnalyticalFeatureNamePipe } from '@zaoblako/web/shared/pipes/contract-analytical-feature-name.pipe';
import { ContractAnalyticalFeatureEnum } from '@zaoblako/shared/modules/document/enums/contract-analytical-feature.enum';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { CompanyMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-multi-select/company-multi-select.component';
import { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import type { ContractAnalyticalFeaturesItemInterface } from '@zaoblako/shared/modules/document/dtos/contract-analytical-features/contract-analytical-features-item.dto';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ConditionalRouterLinkDirective } from '@zaoblako/web/shared/directives/conditional-router-link.directive';
import { MessagesModule } from 'primeng/messages';
import { catchError } from 'rxjs/operators';
import { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';

@Component({
  selector: 'crm-group-contract-form',
  standalone: true,
  imports: [
    StepsModule,
    AsyncPipe,
    CardModule,
    DaterangepickerComponent,
    MultiSelectComponent,
    ContractAnalyticalFeatureNamePipe,
    CompanyMultiSelectComponent,
    ContragentMultiSelectComponent,
    CheckboxModule,
    FormsModule,
    ButtonComponent,
    SpinnerComponent,
    TableComponent,
    ConditionalRouterLinkDirective,
    MessagesModule
  ],
  templateUrl: './group-contract-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupContractFormComponent implements OnInit {
  @Input() public readonly index: number;

  public items: ContractAnalyticalFeaturesItemInterface[] = [];

  public activeStep = 0;
  public steps: MenuItem[] = [
    {
      label: 'Поиск договоров'
    },
    {
      label: 'Выбор договоров для заполнения аналитики'
    }
  ];

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public columns: CrmTableColumn[] = [];

  public contractQueryParams: ContractAnalyticalFeaturesQueryInterface = {
    limit: 200,
    offset: 0
  };

  public readonly contractAnalyticalFeatures = Object.keys(ContractAnalyticalFeatureEnum);

  public selectedCompanies: CompanyFindInterface[] = [];
  public selectedContragents: ContragentFindInterface[] = [];

  public get ContractAnalyticalFeatureEnum() {
    return ContractAnalyticalFeatureEnum;
  }

  public constructor(
    private readonly contractService: ContractService,
    private readonly formStoreService: FormStoreService,
    private readonly filtersService: FiltersService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
  }

  public setDateFilters(dates: [Date, Date]) {
    [this.contractQueryParams.from, this.contractQueryParams.to] = this.filtersService.formatISOPeriod(dates);
  }

  public changeStep() {
    if (!this.activeStep) {
      this.formStoreService.setLoading(true, this.index);

      if (this.selectedCompanies?.length) {
        this.contractQueryParams.companyIds = this.selectedCompanies?.map((company) => company.id);
      } else {
        this.contractQueryParams.companyIds = [];
      }

      if (this.selectedContragents?.length) {
        this.contractQueryParams.contragentIds = this.selectedContragents?.map((contragent) => contragent.id);
      } else {
        this.contractQueryParams.contragentIds = [];
      }

      this.loadAnalytics();
    }
  }

  public toItem(item: any): ContractAnalyticalFeaturesItemInterface {
    return item as ContractAnalyticalFeaturesItemInterface;
  }

  public create() {
    this.formStoreService.setLoading(true, this.index);

    from(this.items)
      .pipe(
        concatMap((item) => {
          return this.contractService.update<ContractFindInterface>(item);
        }),
        toArray(),
        map(() => {
          this.errorService.addSuccessMessage('Аналитика заполненна');
          this.formStoreService.saveNext(null, this.index);
        }),
        catchError((error) => {
          this.errorService.addCustomError(error);
          return of(null);
        })
      )
      .subscribe();
  }

  private loadAnalytics() {
    if (!this.formStoreService.getState.forms[this.index]?.loading) {
      this.formStoreService.setLoading(true, this.index);
    }

    this.contractService.toFillAnalytics(this.contractQueryParams).subscribe({
      next: (data) => {
        this.items = data.items;
        if (this.activeStep !== 1) {
          this.activeStep = 1;
        }
        this.buildColumns();
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  private buildColumns() {
    this.columns = [{ label: 'Договор' }];

    if (this.contractQueryParams.features?.length) {
      this.contractQueryParams.features?.forEach((feature) => {
        this.columns = this.columns.concat({
          label: new ContractAnalyticalFeatureNamePipe().transform(feature)
        });
      });
    } else {
      this.contractAnalyticalFeatures?.forEach((feature) => {
        this.columns = this.columns.concat({
          label: new ContractAnalyticalFeatureNamePipe().transform(feature as any)
        });
      });
    }
  }
}
